import { action, computed, observable } from "mobx";
import FlashMessageStore from "./FlashMessageStore";

class ProcessingImageStore {
  imageStore = null;

  flashMessageStore = null;

  filterConfiguration = [
    {
      id: 0,
      name: "brightness",
      minValue: -100,
      maxValue: 100,
      defaultValue: 0,
    },
    {
      id: 1,
      name: "contrast",
      minValue: -100,
      maxValue: 100,
      defaultValue: 0,
    },
    {
      id: 2,
      name: "saturation",
      minValue: -100,
      maxValue: 100,
      defaultValue: 0,
    },
    {
      id: 3,
      name: "vibrance",
      minValue: -100,
      maxValue: 100,
      defaultValue: 0,
    },
    {
      id: 4,
      name: "exposure",
      minValue: -100,
      maxValue: 100,
      defaultValue: 0,
    },
    {
      id: 5,
      name: "hue",
      minValue: 0,
      maxValue: 100,
      defaultValue: 0,
    },
    {
      id: 6,
      name: "sepia",
      minValue: 0,
      maxValue: 100,
      defaultValue: 0,
    },
    {
      id: 7,
      name: "gamma",
      minValue: 0,
      maxValue: 100,
      defaultValue: 0,
    },
    {
      id: 8,
      name: "noise",
      minValue: 0,
      maxValue: 100,
      defaultValue: 0,
    },
    {
      id: 9,
      name: "clip",
      minValue: 0,
      maxValue: 100,
      defaultValue: 0,
    },
    {
      id: 10,
      name: "sharpen",
      minValue: 0,
      maxValue: 100,
      defaultValue: 0,
    },
    {
      id: 11,
      name: "stackBlur",
      minValue: 0,
      maxValue: 100,
      defaultValue: 0,
    },
  ];

  defaultFilterValues = null;

  @observable showModal = false;

  @observable isImageLoading = false;

  @observable isProcessing = false;

  @observable currentImageIndex = 0;

  @observable filtersBefore = {};

  @observable filters = {};

  constructor(imageStore) {
    this.imageStore = imageStore;
    this.flashMessageStore = new FlashMessageStore();

    // Create the default filter values from configuration
    // Only used for function setDefaultFilters
    this.defaultFilterValues = {};

    this.filterConfiguration.forEach(filter => {
      this.defaultFilterValues[filter.name] = filter.defaultValue;
    });
  }

  @action
  setDefaultFilters() {
    this.filtersBefore = this.filters = this.defaultFilterValues;
  }

  /**
   * Open modal
   * Click from PanoramasSection
   * @param {string} currentImageUrl
   */
  @action
  openModal(currentImageUrl) {
    this.isImageLoading = true;
    this.currentImageIndex = this.imageStore.imageUrls.findIndex(imageUrl => imageUrl === currentImageUrl);
    this.setDefaultFilters();
    this.showModal = true;
  }

  /**
   * Change current image
   * Click from ProcessingImageModal
   * @param {number} currentImageIndex
   */
  @action
  changeCurrentImage(currentImageIndex) {
    this.isImageLoading = true;
    this.currentImageIndex = currentImageIndex;
    this.setDefaultFilters();
  }

  @action
  finishImageLoading() {
    this.isImageLoading = false;
  }

  @action
  startProcessing() {
    this.isProcessing = true;
  }

  @action
  finishProcessing() {
    this.isProcessing = false;
  }

  @action
  changeFilterBefore(filterName, newValue, minValue = 0, maxValue = 100) {
    // For an empty text we allow the change of the input
    if (newValue === "" || newValue === "-") {
      this.filtersBefore[filterName] = newValue;
      return;
    }

    const newValueInt = parseInt(newValue, 10);

    if (!Number.isInteger(newValueInt) || newValueInt < minValue || newValueInt > maxValue) {
      this.filtersBefore[filterName] = this.filters[filterName];
      return;
    }

    this.changeFilter(filterName, newValueInt);
  }

  @action
  changeFilter(filterName, newValue) {
    this.isProcessing = true;

    const newValueInt = parseInt(newValue, 10);

    this.filtersBefore[filterName] = newValueInt;
    this.filters[filterName] = newValueInt;
    this.renderCanvas();
  }

  /**
   * Render canvas
   * When the user changes filter values
   */
  @action
  renderCanvas() {
    const { filterConfiguration } = this;
    const { filters } = this;

    const startProcessingCallback = () => {
      this.startProcessing();
    };

    const finishProcessingCallback = () => {
      this.finishProcessing();
    };

    // eslint-disable-next-line func-names
    window.Caman("#canvas", function() {
      startProcessingCallback();

      this.revert(false);

      for (let i = 0; i < filterConfiguration.length; i += 1) {
        const filter = filterConfiguration[i];

        if (filters[filter.name] !== filter.defaultValue) {
          // same as e.g this.brightness("value");
          this[filter.name](filters[filter.name]);
        }
      }

      // eslint-disable-next-line func-names
      this.render(function() {
        finishProcessingCallback();
      });
    });
  }

  /**
   * Revert canvas
   */
  @action
  revertCanvas() {
    this.setDefaultFilters();

    // eslint-disable-next-line func-names
    window.Caman("#canvas", function() {
      this.revert();
    });
  }

  @action
  updateUploadedImage() {
    this.isProcessing = true;
    this.isImageLoading = true;

    const updateImageDataCallback = imageData => {
      this.imageStore
        .updateImageData(this.imageStore.imageUrls[this.currentImageIndex], imageData)
        .then(() => {
          this.flashMessageStore.setMessage("Image has been updated successfully!");
          this.setDefaultFilters();
          this.isProcessing = false;
        })
        .catch(error => {
          this.flashMessageStore.setMessage(error.message, "negative");
          this.isImageLoading = false;
          this.isProcessing = false;
        });
    };

    // eslint-disable-next-line func-names
    window.Caman("#canvas", function() {
      // eslint-disable-next-line func-names
      this.render(function() {
        updateImageDataCallback(this.toBase64("jpeg"));
      });
    });
  }

  @computed
  get isLoading() {
    return this.isImageLoading || this.isProcessing;
  }

  @computed
  get numberOfImages() {
    return this.imageStore.imageUrls.length;
  }

  @computed
  get currentImageUrl() {
    return this.imageStore.imageUrls[this.currentImageIndex];
  }

  @computed
  get hasChanges() {
    const { filterConfiguration } = this;
    const { filters } = this;

    for (let i = 0; i < filterConfiguration.length; i += 1) {
      const filter = filterConfiguration[i];

      if (filters[filter.name] !== filter.defaultValue) {
        return true;
      }
    }

    return false;
  }
}

export default ProcessingImageStore;
